import {useQuery} from 'react-query';
import {productsComparisonQueryKey, productsQueryKey} from 'constants/query';
import CalculationsServiceApi from 'api/calculations-api';
import {useCallback, useMemo} from 'react';
import {isEqual, isFunction, pick} from 'lodash';
import {useSetProductResult} from 'query/products';
import {useDateOfBirthValidation} from 'hooks/validator';
import {useDispatch} from 'react-redux';
import {setScenarios, removeScenarios} from 'reducers/settings.reducer';

function productIdMap(product) {
  return product?.ProductId;
}

function productFeatureFilter(productFeatureKey, options = {}) {
  const {parse = (enabled) => enabled} = options;

  return function productKeyFind(product) {
    return parse(
      product?.ProductFeatures?.find((productFeature) => {
        return productFeature?.ProductFeatureKey === productFeatureKey;
      })?.Enabled,
    );
  };
}

function useIncludedProductsFilter(props = {}) {
  const {products = [], productFeatureKey, includeAllProducts} = props;

  const includedProducts = useMemo(
    () => (includeAllProducts ? products.map(productIdMap) : products.filter(productFeatureFilter(productFeatureKey)).map(productIdMap)),
    [includeAllProducts, productFeatureKey, products],
  );

  const excludedProducts = useMemo(
    () =>
      includeAllProducts
        ? []
        : products
            .filter(
              productFeatureFilter(productFeatureKey, {
                parse(enabled) {
                  return !enabled;
                },
              }),
            )
            .map(productIdMap),
    [includeAllProducts, productFeatureKey, products],
  );

  const enabled = useMemo(
    () => includeAllProducts ? products.length > 0 : includedProducts?.length > 0 || excludedProducts?.length > 0,
    [includeAllProducts, products.length, includedProducts, excludedProducts],
  );

  return {products: includedProducts, excludedProducts, enabled};
}

function getComparisonQueryKeyData(data = {}) {
  return pick(data, [
    'BorrowerProfileId',
    'IsSaved',
    'OverrideClosingCosts',
    'LenderCredit',
    'FundAccess',
    'Tenure',
    'ClosingCost',
    'CashAtClose',
    'BorrowerProfile',
    'HomeAppreciation',
    'AnnualPayments',
    'MonthlyPayments',
    'RateAdjustments',
  ]);
}

export function useGetCalculationComparisonQuery(props = {}) {
  const {
    onSuccess: onComparisonSuccess,
    enabled: moduleEnabled = true,
    products = [],
    shouldSetComparisonResponse = true,
    productFeatureKey,
    includeAllProducts,
    productsQueryKey: productsCacheKey = productsQueryKey,
    ApiRoute,
    withBorrowerProfile = true,
  } = props;

  const {getData, setProductResponse} = useSetProductResult({productsQueryKey: productsCacheKey});
  const dispatch = useDispatch();

  const data = useMemo(() => getData(undefined, {withBorrowerProfile}), [getData, withBorrowerProfile]);

  const comparisonQueryData = useMemo(() => {
    const queryData = getComparisonQueryKeyData(data);

    return {
      ...queryData,
      BorrowerProfile: queryData.BorrowerProfile
        ? pick(queryData.BorrowerProfile, [
            'BorrowerProfileId',
            'DateOfBirth',
            'Zip',
            'State',
            'ValueOfProperty',
            'Liens',
            'FirstLien',
            'OtherLiens',
            'IsThere2nd',
            'NumberOfUnits',
            'Age',
          ])
        : queryData.BorrowerProfile,
    };
  }, [data]);

  const onSuccess = useCallback(
    (responseData) => {
      const productsToSet = [];

      if (responseData.Calculations?.length) {
        productsToSet.push(...responseData.Calculations);
      }
      if (responseData.Errors?.length) {
        productsToSet.push(
          ...responseData.Errors.map((error) => ({
            ProductId: products.find((product) => product.ApiRoute === error.Calculation)?.ProductId,
            CalculationId: '',
            BorrowerProfileId: '',
            Errors: [{ErrorMessage: error.Message}],
          })),
        );
      }

      window.setTimeout(() => {
        if (!responseData.Scenarios?.length) {
          dispatch(removeScenarios());
        }

        if (responseData.Scenarios?.length) {
          dispatch(setScenarios(responseData.Scenarios));
        }

        let productResponse = undefined;
        if (shouldSetComparisonResponse) {
          productResponse = setProductResponse(productsToSet, {ApiRoute});
        }

        if (isFunction(onComparisonSuccess)) onComparisonSuccess(responseData, productsToSet, productResponse);
      }, 0);
    },
    [shouldSetComparisonResponse, setProductResponse, ApiRoute, onComparisonSuccess, products, dispatch],
  );

  const {enabled: includedExcludedEnabled, products: includedProducts, excludedProducts} = useIncludedProductsFilter({
    products,
    productFeatureKey,
    includeAllProducts,
  });

  const includedExcludedProducts = useMemo(
    () => ({products: includedProducts, excludedProducts}),
    [includedProducts, excludedProducts],
  );

  const dateOfBirthValidation = useDateOfBirthValidation({
    Zip: data?.BorrowerProfile?.Zip,
    ApiRoute,
  });

  const enabled = useMemo(() => {
    if (!moduleEnabled) return false;
    if (!data?.BorrowerProfileId) {
      try {
        dateOfBirthValidation.validateSync(data?.BorrowerProfile?.DateOfBirth);
      } catch (e) {
        return false;
      }
    }

    return includedExcludedEnabled;
  }, [dateOfBirthValidation, moduleEnabled, data, includedExcludedEnabled]);

  return useQuery({
    queryKey: [productsComparisonQueryKey, comparisonQueryData, productFeatureKey, includeAllProducts, includedExcludedProducts],
    queryFn() {
      return CalculationsServiceApi.getProductComparison({...data, ...includedExcludedProducts});
    },
    enabled,
    onSuccess,
    refetchOnMount: false,
    structuralSharing: (previousData, newData) => (isEqual(previousData, newData) ? previousData : newData),
  });
}
